import { useState, useEffect, useCallback } from 'react';
import { TextField, Box, CircularProgress, Autocomplete, Button, IconButton } from '@mui/material';
import { Restore } from '@mui/icons-material';
import { connect } from 'react-redux';
import ReactEcharts from '../../lazy/Echarts';
import { meanStd } from '../../../utils/math';
import RangePicker from '../../trends/RangePicker';

const VisualSummary = ({ site, data }) => {
  const [echartsRef, setEchartsRef] = useState(null);
  const [selection, setSelection] = useState([]);
  const [{ start, end }, setRange] = useState({});

  const resetTime = useCallback(() => {
    const newStart = data[0]?.Timestamp;
    const newEnd = data[(data.length || 1) - 1]?.Timestamp;
    setRange({ start: Date.parse(newStart), end: Date.parse(newEnd) });
  }, [data]);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(resetTime, [data, resetTime]);
  if (!data) {
    return (
      <Box sx={{ display: 'grid', justifyContent: 'center' }}>
        <CircularProgress size="150px" />
      </Box>
    );
  }

  const columns = Object.keys(data[0] ?? {});
  const series = columns
    .filter((name) => name !== 'Timestamp')
    .filter((name) => selection.includes(name))
    .map((c) => {
      const { mean, stdev } = meanStd(data.map((r) => r[c]).filter(Boolean));
      const min = mean - 3 * stdev;
      const max = mean + 3 * stdev;
      return {
        encode: { x: 'Timestamp', y: c },
        name: c,
        type: 'line',
        markLine: {
          symbol: ['none', 'none'],
          data: [
            { name: 'min', label: { show: false }, yAxis: min },
            { name: 'max', label: { show: false }, yAxis: max },
          ],
        },
      };
    });

  const option = {
    grid: {
      left: '3%',
      right: '4%',
    },
    legend: {
      type: 'scroll',
      orient: 'horizontal',
    },
    tooltip: {
      trigger: 'axis',
    },
    dataset: {
      // Provide a set of data.
      dimensions: columns,
      source: data,
    },
    // Declare an x-axis (time axis).
    xAxis: {
      type: 'time',
      min: start,
      max: end,
    },
    // Declare a y-axis (value axis).
    yAxis: { type: 'value' },
    series,
  };

  return (
    <>
      <Autocomplete
        onChange={(_event, newValue) => {
          setSelection(newValue);
        }}
        value={selection}
        multiple
        options={columns.filter((c) => c !== 'Timestamp')}
        // eslint-disable-next-line react/jsx-props-no-spreading
        renderInput={(params) => <TextField {...params} label="Please Select Data Points" />}
      />
      {
        // RangePicker doesn't deal with start end being set after initial render, so just hacking around
        !Number.isNaN(start) && !Number.isNaN(end) ? (
          <>
            <RangePicker
              from={start}
              to={end}
              timezone="America/New_York"
              onChange={({ from, to }) => setRange({ start: from, end: to })}
            />
            <IconButton onClick={resetTime}>
              <Restore />
            </IconButton>
          </>
        ) : null
      }
      {echartsRef === null || selection.length === 0 ? null : (
        <Button
          variant="contained"
          sx={{ marginLeft: '30px' }}
          onClick={() => {
            const image = echartsRef
              .getEchartsInstance()
              .getDataURL({ pixelRatio: 2, backgroundColor: '#fff' });
            const a = document.createElement('a');
            a.setAttribute('download', `nyserda-${site}-${Date.now()}.png`);
            a.href = image;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }}
        >
          Download as Image
        </Button>
      )}
      <ReactEcharts ref={setEchartsRef} option={option} notMerge />
    </>
  );
};

export default connect()(VisualSummary);
